/**
 * Dashboard Screen
 *
 * Post-analysis summary view showing key portfolio metrics at a glance:
 *  - KPI cards: reference date, number of suppliers, total outstanding
 *    invoices and amount.
 *  - Supplier summary table + bar chart of outstanding AR by supplier.
 *  - Three histogram breakdowns of the invoice population:
 *    (a) balance amount by range, (b) invoice count by range,
 *    (c) invoice amount by range.
 *
 * Data flow:
 *  - `getDashboardData` fetches pre-aggregated summary and histogram data
 *    from the Python backend on mount.
 *  - Download buttons export the eligibility report (Excel) and all
 *    analysis parquet/CSV files.
 *  - Uses Recharts BarChart with a custom triangle-shaped bar renderer.
 */
import { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  ResponsiveContainer,
  YAxis,
  CartesianGrid,
} from "recharts";
// -------------------------------------------------------------
import { downloadReport, downloadParquets } from "System/system";
import { getDashboardData } from "Redux/dashboard/actions";
import DataTable from "Components/DataTable";
import Number from "Components/Number";
import useCountUp from "Auxiliary/useCountUp";

// -------------------------------------------------------------
const headers = {
  dashboard: [
    {
      text: "Supplier",
      key: "Supplier",
      datatype: "string",
    },
    {
      text: "Outstanding Invoices",
      key: "Outstanding Invoices",
      datatype: "int",
    },
    {
      text: "Outstanding Net AR Balance",
      key: "Outstanding Net AR Balance",
      datatype: "float",
    },
  ],
  histogram_a: [
    {
      text: "Range",
      key: "Range",
      datatype: "string",
    },
    {
      text: "Balance Count",
      key: "Count",
      datatype: "int",
    },
    {
      text: "Balance Count Percent",
      key: "Percentage",
      datatype: "float",
      factor: 100,
      display: "percentage",
    },
    {
      text: "Balance Amount",
      key: "Amount",
      datatype: "float",
    },
    {
      text: "Balance Amount Percent",
      key: "Amount Percentage",
      datatype: "float",
      display: "percentage",
      factor: 100,
    },
  ],
  histogram_b: [
    {
      text: "Range",
      key: "Range",
      datatype: "string",
    },
    {
      text: "Invoice Count",
      key: "Invoice Count",
      datatype: "int",
    },
    {
      text: "Invoice Count Percent",
      key: "Invoice Count Percentage",
      datatype: "float",
      display: "percentage",
      factor: 100,
    },
  ],
  histogram_c: [
    {
      text: "Range",
      key: "Range",
      datatype: "string",
    },
    {
      text: "Invoice Amount",
      key: "Invoice Amount",
      datatype: "float",
    },
    {
      text: "Invoice Amount Percent",
      key: "Invoice Amount Percentage",
      datatype: "float",
      display: "percentage",
      factor: 100,
    },
  ],
};
const COLORS = [
  "#0088FE", // Blue
  "#00C49F", // Teal
  "#FFBB28", // Yellow
  "#FF8042", // Orange
  "#A28BD9", // Purple
  "#50D050", // Green
  "#FF6161", // Red
  "#8A2BE2", // Indigo
  "#FF69B4", // Pink
  "#FFD700", // Gold
];

// SVG path for a triangle/teardrop bar shape used by Recharts custom bar renderer.
const getPath = (x, y, width, height) => {
  return `M${x},${y + height}C${x + width / 3},${y + height} ${x + width / 2},${
    y + height / 3
  }
  ${x + width / 2}, ${y}
  C${x + width / 2},${y + height / 3} ${x + (2 * width) / 3},${y + height} ${
    x + width
  }, ${y + height}
  Z`;
};

const TriangleBar = (props) => {
  const { fill, x, y, width, height } = props;

  return <path d={getPath(x, y, width, height)} stroke="none" fill={fill} />;
};

// -------------------------------------------------------------
// -------------------------------------------------------------
const Dashboard = () => {
  const dispatch = useDispatch();
  const project = useSelector((state) => state.project);
  const { dashboard } = useSelector((state) => state.dashboard);
  const projectid = project.id;

  useEffect(() => {
    dispatch(
      getDashboardData(() => {
        console.log("done getting dashboard data");
      })
    );
  }, [projectid, dispatch]);

  if (!dashboard)
    return (
      <div>
        <div className="buttons box" style={{ display: "none" }}>
          <button
            className="button is-small"
            onClick={() => downloadReport(projectid)}
          >
            <span className="icon">
              <i className="fa-duotone fa-file-spreadsheet" />
            </span>
            <span>Download Eligibility Report</span>
          </button>
        </div>
      </div>
    );

  const summaryData = dashboard.summary.data.map((item) => {
    return {
      supplier: item.Supplier,
      number: item["Outstanding Invoices"],
      value: item["Outstanding Net AR Balance"],
    };
  });

  const histogramData_a = dashboard.histogram.data.map((item) => {
    return {
      range: item.Range,
      value: item.Amount,
    };
  });

  const histogramData_b = dashboard.histogram.data.map((item) => {
    return {
      range: item.Range,
      value: item["Invoice Count"],
    };
  });

  const histogramData_c = dashboard.histogram.data.map((item) => {
    return {
      range: item.Range,
      value: item["Invoice Amount"],
    };
  });

  return (
    <div>
      <div className="buttons box">
        <button
          className="button is-small"
          onClick={() => downloadParquets(projectid)}
        >
          <span className="icon">
            <i className="fa-duotone fa-download" />
          </span>
          <span>Download All Analysis Files</span>
        </button>
      </div>
      <div className="columns">
        <DashboardItem headline="Date" value="10-31-2023" isReference={true} />
        <DashboardItem
          headline="Number of Suppliers"
          value={dashboard.summary.data.length}
        />
        <DashboardItem
          headline="Outstanding Invoices"
          value={dashboard.summary.data.reduce(
            (acc, item) => acc + item["Outstanding Invoices"],
            0
          )}
        />
        <DashboardItem
          headline="Outstanding Amount"
          value={dashboard.summary.data.reduce(
            (acc, item) => acc + item["Outstanding Net AR Balance"],
            0
          )}
          isRounded={true}
        />
      </div>

      <div className="box">
        <div className="columns">
          <div className="column">
            <DataTable
              data={dashboard.summary.data}
              headers={headers.dashboard}
              reference="Supplier"
            />
          </div>
          <div className="column">
            <div style={{ height: "100%" }}>
              <div className="dashboard-headline">Outstanding Amount</div>
              <ResponsiveContainer width="90%" height="90%">
                <BarChart
                  width="100%"
                  height="100%"
                  data={summaryData}
                  margin={{
                    top: 5,
                    right: 5,
                    left: 35,
                    bottom: 5,
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="supplier" />
                  <YAxis />
                  <Bar dataKey="value" fill="#8884d8" shape={<TriangleBar />}>
                    {summaryData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % 20]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>

      <div className="box">
        <div className="columns">
          <div className="column">
            <DataTable
              data={dashboard.histogram.data}
              headers={headers.histogram_a}
              reference="Range"
            />
          </div>
          <div className="column">
            <div style={{ height: "100%" }}>
              <div className="dashboard-headline">Balance Amount</div>
              <ResponsiveContainer width="90%" height="90%">
                <BarChart
                  width="100%"
                  height="100%"
                  data={histogramData_a}
                  margin={{
                    top: 5,
                    right: 5,
                    left: 35,
                    bottom: 5,
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="range" />
                  <YAxis />
                  <Bar dataKey="value" fill="#8884d8">
                    {histogramData_a.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={"#8884d8"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>

      <div className="box">
        <div className="columns">
          <div className="column">
            <DataTable
              data={dashboard.histogram.data}
              headers={headers.histogram_b}
              reference="Range"
            />
          </div>
          <div className="column">
            <div style={{ height: "100%" }}>
              <div className="dashboard-headline">Invoice Count</div>
              <ResponsiveContainer width="90%" height="90%">
                <BarChart
                  width="100%"
                  height="100%"
                  data={histogramData_b}
                  margin={{
                    top: 5,
                    right: 5,
                    left: 35,
                    bottom: 5,
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="range" />
                  <YAxis />
                  <Bar dataKey="value" fill="#8884d8">
                    {histogramData_b.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={"#8884d8"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>

      <div className="box">
        <div className="columns">
          <div className="column">
            <DataTable
              data={dashboard.histogram.data}
              headers={headers.histogram_c}
              reference="Range"
            />
          </div>
          <div className="column">
            <div style={{ height: "100%" }}>
              <div className="dashboard-headline">Invoice Amount</div>
              <ResponsiveContainer width="90%" height="90%">
                <BarChart
                  width="100%"
                  height="100%"
                  data={histogramData_c}
                  margin={{
                    top: 5,
                    right: 5,
                    left: 35,
                    bottom: 5,
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="range" />
                  <YAxis />
                  <Bar dataKey="value" fill="#8884d8">
                    {histogramData_c.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={"#8884d8"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;

// KPI card component. `isReference` renders the value as plain text (for dates);
// otherwise uses the Number formatter (with optional rounding for currency amounts).
const DashboardItem = ({ headline, value, isRounded, isReference }) => {
  // Roll the figure up from 0 on load (and old → new on scenario changes).
  const animated = useCountUp(isReference ? 0 : value);
  return (
    <div className="column">
      <div className="box">
        <div className="dashboard-headline">{headline}</div>
        {isReference ? (
          <div className="large-number">{value}</div>
        ) : (
          <div className="large-number">
            <Number value={animated} isRounded={isRounded} />
          </div>
        )}
      </div>
    </div>
  );
};
